import {
  AppointmentStatusCodes,
  CreateCaseDto,
  DataBlockTypeDto,
  DataBlockVariant,
  DeviceModel,
  MeasurementMappingDto,
  PatientDto,
  PatientListDto,
  QueryFactory,
  ReadFileData,
  useEnabledDataBlockTypes,
  VisitDto,
  VisitListDto,
} from '@lmt/shared-types';
import { useTabs } from '@lmt/unicorn-shared/app/features/navigation/operations';
import SettingsIcon from '@lmt/unicorn-shared/app/icons/gear.svg?react';
import { useEventCallback } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { startOfDay } from 'date-fns';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { pages } from '../../constants/sharedRoutes';
import { useClaims } from '../../features/auth/operations';
import { useVisitTypes } from '../../features/dictionaries/selectors';
import { PtsActions } from '../../features/pts/reducer';
import { getImportFile, getPdfFile } from '../../features/pts/selectors';
import { CaseData, useCases } from '../../helpers/case-queries';
import {
  localizeCaseTitle,
  localizeVisitType,
} from '../../helpers/localization/translations';
import { useScopedTranslation } from '../../helpers/localization/useScopedTranslation';
import { useUniSelector } from '../../store/types';
import { Field } from '../common/Field';
import { PatientSearch } from '../common/patient/PatientSearch';
import { Button, ButtonColor } from '../common/ui-kit/buttons/Button';
import { IconButton } from '../common/ui-kit/IconButton';
import { DropDownInput } from '../common/ui-kit/inputs/dropdown/DropDownInput';
import { useModal } from '../common/ui-kit/modal/useModal';
import { FormComponentProps } from '../global-forms/GlobalForms.types';
import { useGlobalForms } from '../global-forms/useGlobalForms';
import { getObjectHash } from '../merge-assistant/form/utils/hash-utils';
import { InputComponentForPatientSearch } from '../miniscreen-importer/components/InputComponentForPatientSearch';
import { RadioButton } from '../miniscreen-importer/components/RadioButton';
import {
  ImportData,
  importMeasurement,
} from '../miniscreen-importer/utils/import';
import { getFormattedDateTime } from '../patients/common';
import styles from './PtsImporter.module.scss';
import { mapMeasuredValues } from './utils/mapping';
import {
  getEndDateFromFromMeasuredValues,
  getStartDateFromMeasuredValues,
  parseData,
} from './utils/parsing';
import { MeasuredValue } from './utils/types';

interface VisitOption {
  id: number;
  caseId: number;
  title: string;
  start: Date;
}

function getVisitOption(
  visit: VisitListDto | VisitDto,
  visitTitle: string,
): VisitOption {
  return {
    id: visit.id,
    caseId: visit.caseId,
    title: `${getFormattedDateTime(visit.start)} ${visitTitle}`,
    start: visit.start,
  };
}

const PtsImporter: FC<FormComponentProps> = (props) => {
  const { formType, formId, tryCompleteForm, loadBlocking } = props;
  const i18next = useScopedTranslation('pts');
  const i18nextGlobal = useTranslation();
  const globalForms = useGlobalForms();
  const modals = useModal();
  const claims = useClaims();
  const tabs = useTabs();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const [isDirty, setDirty] = useState<boolean>(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  const readFile = useUniSelector((state) => state.pts.readFile);
  const importFile = useUniSelector(getImportFile);
  const pdfFile = useUniSelector(getPdfFile);

  const { data: importSettings, isLoading: isSettingsQueryLoading } =
    QueryFactory.MeasurementImportSettingsQuery.useGetQuery(DeviceModel.PTSLab);

  //#region types, mappings, fields
  const dataBlockTypes = useEnabledDataBlockTypes();
  const [selectedMeasurementType, setMeasurementType] = useState<
    DataBlockTypeDto | undefined
  >(undefined);

  const measurementTypes = useMemo(
    () =>
      dataBlockTypes?.filter(
        (x) =>
          !x.isDeactivated &&
          x.variant === DataBlockVariant.DeviceMeasurement &&
          x.device === DeviceModel.PTSLab,
      ) ?? [],
    [dataBlockTypes],
  );

  const measurementFieldsQuery = QueryFactory.DataBlockFieldQuery.useGetQuery(
    { dataBlockTypeId: selectedMeasurementType?.id },
    {
      enabled: !!selectedMeasurementType,
    },
  );

  const mappingsQuery = QueryFactory.MeasurementMappingQuery.useGetQuery(
    {
      measurementTypeId: selectedMeasurementType?.id ?? 0,
      deviceModel: DeviceModel.PTSLab,
    },
    {
      enabled: !!selectedMeasurementType,
      select: useCallback(
        (data: MeasurementMappingDto[]) => data.filter((x) => x.isActive),
        [],
      ),
    },
  );

  const openMappings = useEventCallback(() => {
    tabs.openTab(
      pages.mappingsWithDeviceAndMeasurement.link({
        deviceId: DeviceModel.MiniScreen,
        measurementTypeId: selectedMeasurementType?.id,
      }),
    );
  });
  //#endregion

  //#region patient, case, visit
  const [selectedPatient, setSelectedPatient] = useState<
    PatientListDto | PatientDto | null
  >(null);
  const [currentCase, setCurrentCase] = useState<CaseData | null>(null);
  const [currentVisit, setCurrentVisit] = useState<VisitOption | null>(null);

  const casesQuery = useCases(selectedPatient?.id);
  const cases: CaseData[] = useMemo(
    () => casesQuery?.data ?? [],
    [casesQuery?.data],
  );

  const visitTypes = useVisitTypes();

  const visitsQuery = QueryFactory.AppointmentEventsQuery.useSearchVisitsQuery(
    {
      patientId: selectedPatient?.id,
    },
    { enabled: !!selectedPatient?.id },
  );

  const visits: VisitOption[] = useMemo(() => {
    if (!visitsQuery.data) {
      return [];
    }
    return (
      visitsQuery.data
        ?.filter(
          (x) =>
            x.statusId !== AppointmentStatusCodes.Canceled &&
            x.statusId !== AppointmentStatusCodes.Skipped,
        )
        .filter((x) => (currentCase ? x.caseId === currentCase.id : true))
        .map((x) =>
          getVisitOption(x, localizeVisitType(visitTypes, x.typeId)),
        ) ?? []
    );
  }, [
    visitsQuery.data,
    visitsQuery.isStale,
    visitsQuery.isFetched,
    visitTypes,
    currentCase,
  ]);

  const addNewPatient = useEventCallback(async () => {
    const createdPatient = await globalForms.openPatientCreating();
    if (createdPatient) {
      setSelectedPatient(createdPatient);
    }
  });

  const addNewCase = useEventCallback(
    async (patientId?: number, startDate?: Date) => {
      if (!patientId) {
        return;
      }

      try {
        const newCase: CreateCaseDto = {
          patientId: patientId,
          startDate: startDate ?? startOfDay(new Date()),
          endDate: null,
        };
        const createdCase = await QueryFactory.CaseClient.createCase(newCase);
        const createdCaseData = {
          ...createdCase,
          title: localizeCaseTitle(createdCase),
          isCompleted: false,
        };

        await casesQuery.refetch();

        setTimeout(() => setCurrentCase(createdCaseData), 100);
      } catch (e) {
        console.error('addNewCase', e);
        await modals.showError({
          text: i18next.t('errors.failed_to_create_case'),
        });
      }
    },
  );

  const addNewVisit = useEventCallback(
    async (patientId?: number, caseId?: number) => {
      const newVisit = await globalForms.openVisitCreating(
        undefined,
        undefined,
        patientId,
        caseId,
      );
      if (newVisit) {
        setCurrentVisit(
          getVisitOption(
            newVisit,
            localizeVisitType(visitTypes, newVisit.typeId),
          ),
        );
      }
    },
  );
  //#endregion

  //#region Parse file
  const [measurementValues, setMeasurementValues] = useState<MeasuredValue[]>(
    [],
  );
  const [startDate, setStartDate] = useState<Date>();
  const [endDate, setEndDate] = useState<Date>();

  function parseFile(readFile: ReadFileData) {
    if (!readFile) {
      return;
    }
    if (readFile.status === 'error') {
      setError(new Error("Can't read the file."));
    } else {
      try {
        const result = parseData(readFile.data);
        if (!result.validation.isValid) {
          setError(new Error(result.validation.error));
        } else {
          setMeasurementValues(result.measurements);
          return result.measurements;
        }
      } catch (e: any) {
        setError(e);
      }
    }
  }

  function processData(data: MeasuredValue[]) {
    try {
      const start = getStartDateFromMeasuredValues(data);
      const end = getEndDateFromFromMeasuredValues(data);
      setStartDate(start);
      setEndDate(end);
      if (measurementTypes.length > 0) {
        setMeasurementType(measurementTypes[0]);
      }
      // ToDo: find patients
    } catch (e: any) {
      setError(new Error(e));
    }
  }

  useEffect(
    function parseAndProcessData() {
      setError(undefined);
      if (!claims.permissions.importMeasurements) {
        setError(new Error(i18next.t('errors.no_permission')));
        return;
      }
      if (!readFile) {
        return;
      }
      loadBlocking(formId, i18next.t('loading'), async () => {
        if (measurementTypes.length > 0) {
          const parsedMeasurements = parseFile(readFile);
          if (parsedMeasurements) {
            processData(parsedMeasurements);
          }
        }
      }).catch((e) => console.error('parseAndProcessData', e));
    },
    [loadBlocking, readFile, claims.permissions.importMeasurements, formId],
  );

  //#endregion

  //#region import
  const openVisitOrMeasurement = useEventCallback(
    (
      patientId: number,
      caseId: number,
      visitId: number,
      measurementId?: number,
    ) => {
      const visitPrefix = `visit_${visitId}`;
      const measurementBlock = measurementId
        ? `_measurement_${measurementId}`
        : '';
      const item = `${visitPrefix}${measurementBlock}`;
      const search = `item=${item}`;
      const link = pages.patientCases.link({ patientId, caseId });
      tabs.openTab(link, search);
    },
  );

  const importPtsMeasurement = useEventCallback(async () => {
    setDirty(true);
    if (
      !readFile ||
      !startDate ||
      !endDate ||
      !selectedPatient ||
      !currentCase ||
      !currentVisit
    ) {
      return;
    }
    try {
      await tryCompleteForm(formType, formId, async () => {
        const values: Record<string, string | number> = mapMeasuredValues(
          measurementValues,
          mappingsQuery.data ?? [],
          measurementFieldsQuery.data ?? [],
        );
        const valuesHash = getObjectHash(values);
        const data: ImportData = {
          isReImport: false,
          replacedMeasurement: null,
          patientId: selectedPatient.id,
          caseId: currentCase.id,
          visitId: currentVisit?.id,
          deviceModel: DeviceModel.PTSLab,
          typeId: selectedMeasurementType!.id,
          values: values,
          sdbdataFilePath: readFile!.path,
          pdfFilePath: '',
          pdfFile: pdfFile ?? undefined,
          pdfFileName: `${selectedMeasurementType!.value}.pdf`,
          startDate: startDate,
          endDate: endDate,
          file: importFile,
          isStatisticCompleted: selectedMeasurementType?.showStatistic ?? false,
          valuesHash: valuesHash,
          settings: importSettings!,
        };
        console.log(data);
        const measurementId = await importMeasurement(data);
        dispatch(PtsActions.importFinished());
        await queryClient.invalidateQueries({
          queryKey: QueryFactory.DataBlockQuery.getQueryKey(
            null,
            currentCase.id,
            null,
            null,
          ),
        });
        await queryClient.invalidateQueries({
          queryKey:
            QueryFactory.DataBlockQuery.getDataBlockQueryKey(measurementId),
        });
        await queryClient.invalidateQueries({
          queryKey: QueryFactory.DataBlockQuery.getQueryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: QueryFactory.FileQuery.searchFilesQueryKey({
            dataBlockId: measurementId,
          }),
        });
        toast(i18next.t('toasts.import_successful'), {
          type: 'success',
        });
        openVisitOrMeasurement(
          selectedPatient.id,
          currentCase.id,
          currentVisit?.id,
          measurementId,
        );
      });
    } catch (error) {
      console.error('importPtsMeasurement', error);
    }
  });
  //#endregion

  //#region ptslab form
  const onCancel = useCallback(async () => {
    await tryCompleteForm(formType, formId, async () => undefined);
  }, [formType, formId, tryCompleteForm]);

  return (
    <div className={styles.container} data-test-id="pts-import-popup">
      <div className={styles.content}>
        <div className={styles.patientData}>
          <Field
            title={i18next.t('patient')}
            linkProps={{
              title: i18next.t('controls.add_patient'),
              onClick: () => addNewPatient(),
            }}
          >
            <PatientSearch
              isOnlyActive={true}
              onSelectedPatientChanged={(selectedPatient) => {
                setSelectedPatient(selectedPatient as any);
                setCurrentCase(null);
                setCurrentVisit(null);
              }}
              selectedPatient={selectedPatient}
              renderInput={InputComponentForPatientSearch}
              patientsLimit={9}
              errorText={
                isDirty && !selectedPatient
                  ? i18nextGlobal.t('errors.required')
                  : undefined
              }
              autoFocus={true}
              variant={'formInput'}
            />
          </Field>
          <Field
            className={styles.field}
            title={i18next.t('case')}
            linkProps={{
              disabled: !selectedPatient,
              title: i18next.t('controls.add_case'),
              onClick: () => {
                void addNewCase(selectedPatient?.id);
              },
            }}
          >
            <DropDownInput
              useIdFunctionAsValue={true}
              required
              variant="formInput"
              getOptionLabel={'title'}
              idFunction={'id'}
              options={cases}
              getOptionDisabled={(item) => item.isClosed}
              value={currentCase?.id || null}
              onValueChanged={(selectedCase) => {
                setCurrentCase(
                  cases.find(
                    (patientCase) => patientCase.id === selectedCase.id,
                  ) ?? null,
                );
              }}
              errorText={
                isDirty && !currentCase
                  ? i18nextGlobal.t('errors.required')
                  : undefined
              }
            />
          </Field>
          <Field
            className={styles.field}
            title={i18next.t('visit')}
            linkProps={{
              disabled: !selectedPatient || !currentCase,
              title: i18next.t('controls.add_visit'),
              onClick: () => addNewVisit(selectedPatient?.id, currentCase?.id),
            }}
          >
            <DropDownInput
              useIdFunctionAsValue={true}
              required={false}
              variant="formInput"
              getOptionLabel="title"
              idFunction="id"
              options={visits}
              value={currentVisit?.id || null}
              onValueChanged={(selectedVisit) => setCurrentVisit(selectedVisit)}
              errorText={
                isDirty && !currentVisit
                  ? i18nextGlobal.t('errors.required')
                  : undefined
              }
            />
          </Field>
        </div>
        <div className={styles.settings}>
          <div className={styles.settingsTitleContainer}>
            <div className={styles.settingsTitle}>
              {i18next.t('settings_title')}
            </div>
            {!!claims.permissions.editMeasurementMappings &&
              !!selectedMeasurementType && (
                <IconButton
                  color="grey"
                  iconComponent={
                    <SettingsIcon className={styles.settingsIcon} />
                  }
                  className={styles.settingsButton}
                  onClick={openMappings}
                />
              )}
          </div>
          <div className={styles.radioButtonGroup}>
            {measurementTypes.map((measurementType, index) => (
              <RadioButton
                position={
                  index === 0
                    ? 'left'
                    : index === measurementTypes.length - 1
                      ? 'right'
                      : 'center'
                }
                title={measurementType.value}
                key={measurementType.id}
                selected={selectedMeasurementType?.id === measurementType.id}
                onChange={() => setMeasurementType(measurementType)}
              />
            ))}
          </div>
        </div>
      </div>
      <div className={styles.import} data-test-id="button-container">
        <div className={styles.buttonContainer}>
          <Button
            title={i18next.t('controls.cancel_button')}
            color={ButtonColor.Primary}
            onClick={onCancel}
          />
          <Button
            title={i18next.t('controls.import_button')}
            color={ButtonColor.Default}
            className={styles.importButton}
            onClick={importPtsMeasurement}
            disabled={
              !readFile ||
              !!error ||
              measurementValues.length === 0 ||
              !selectedPatient ||
              !currentCase ||
              !currentVisit
            }
          />
        </div>
      </div>
    </div>
  );
  //#endregion
};

export default PtsImporter;
